<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageContext } from '../provider.js';
import Icon from 'next/icon/Icon.vue';
import BaseBubble from 'next/message/bubbles/Base.vue';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { MESSAGE_VARIANTS, ATTACHMENT_TYPES } from '../constants';

const emit = defineEmits(['error']);
const { t } = useI18n();
const { variant, content, contentAttributes, attachments } =
  useMessageContext();

const attachment = computed(() => {
  return attachments.value[0];
});

const isStoryReply = computed(() => {
  return contentAttributes.value?.imageType === ATTACHMENT_TYPES.IG_STORY_REPLY;
});

const hasImgStoryError = ref(false);
const hasVideoStoryError = ref(false);

const formattedContent = computed(() => {
  if (variant.value === MESSAGE_VARIANTS.ACTIVITY) {
    return content.value;
  }

  return new MessageFormatter(content.value).formattedMessage;
});

const onImageLoadError = () => {
  hasImgStoryError.value = true;
  emit('error');
};

const onVideoLoadError = () => {
  hasVideoStoryError.value = true;
  emit('error');
};
</script>

<template>
  <BaseBubble class="sd-social-card overflow-hidden" data-bubble-name="ig-story">
    <div class="sd-social-card__header">
      <p class="sd-social-card__source">
        <span class="i-woot-instagram-color size-3.5" aria-hidden="true" />
        {{ t('SARRDEH.STORY.LABEL') }}
      </p>
      <p v-if="isStoryReply" class="sd-social-card__context">
        {{ t('COMPONENTS.FILE_BUBBLE.INSTAGRAM_STORY_REPLY') }}
      </p>
      <p v-else-if="!content" class="sd-social-card__context">
        {{ t('SARRDEH.STORY.MENTION') }}
      </p>
      <div
        v-if="content"
        v-dompurify-html="formattedContent"
        class="sd-social-card__text"
        dir="auto"
      />
    </div>
    <div class="sd-social-card__media">
      <img
        v-if="!hasImgStoryError"
        class="skip-context-menu"
        alt=""
        loading="lazy"
        :src="attachment.dataUrl"
        @error="onImageLoadError"
      />
      <video
        v-else-if="!hasVideoStoryError"
        class="skip-context-menu"
        controls
        :src="attachment.dataUrl"
        @error="onVideoLoadError"
      />
      <div v-else class="sd-social-card__missing">
        <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
        <p class="mb-0 text-n-slate-11">
          {{ $t('COMPONENTS.FILE_BUBBLE.INSTAGRAM_STORY_UNAVAILABLE') }}
        </p>
      </div>
    </div>
  </BaseBubble>
</template>
